import { ArticleJsonLd } from 'next-seo'
import styled from 'styled-components'
import Image from 'next/image'
// import { contentfulLoader } from '@delicious-simplicity/next-image-contentful-loader'

import {
  getAllVerticals,
  getAllArticleStaticPaths,
  getArticleBySlug,
  getVertical,
} from 'src/utils/contentful'
import wait from 'src/utils/wait'
import Container from 'src/styles/Container'
// import ContentMarkdown from "src/components/ContentMarkdown";
import { H1, H3, SectionHeading, SubHeading1, TextBody } from 'src/styles/Typography'
import { StyledAnchor } from 'src/styles/Button'
import RichTextResponse from 'src/components/RichText'
import Layout from 'src/components/Layout'
import KeyTakeaways from 'src/components/Article/KeyTakeaways'
import TableOfContents from 'src/components/Article/TableOfContents'
import Author from 'src/components/Article/Author'
import grabColor from 'src/utils/grabColor'
import SvgRxIcon from 'src/components/icons/RxIcon'
import Link from 'next/link'

const Post = ({
  fullBody,
  photo,
  shortDescription,
  sys: { publishedAt, firstPublishedAt },
  title,
  verticals,
  vertical,
  author,
  seo,
  slug,
  keyTakeaways,
  expertVerified,
}) => {
  return (
    // TODO: pass article category to Layout -> Header for category breadcrumb
    <Layout
      verticals={verticals}
      vertical={vertical}
      seo={{
        title: title,
        ...seo,
      }}
    >
      <main>
        <article>
          <ArticleJsonLd
            url={`https://policyscout.com/${vertical.slug}/learn/${slug}`}
            title={title}
            images={photo?.url ? [photo.url] : []}
            datePublished={firstPublishedAt}
            dateModified={publishedAt}
            authorName={[author?.fullName || 'Allison Jameson']}
            publisherName="PolicyScout"
            publisherLogo="https://policyscout.com/logo-policyscout.png"
            description={shortDescription}
          />
          <Container>
            <HeadingWrapper>
              <StyledH1>{title}</StyledH1>
              <StyledSubHeading>{shortDescription}</StyledSubHeading>
              <Author
                author={author}
                publishedAt={publishedAt}
                expertVerified={expertVerified}
              />
            </HeadingWrapper>
          </Container>
          {photo?.url && (
            <MainImageWrapper>
              <Container>
                <MainImageContainer>
                  <Image
                    src={`${photo.url}&w=1000&h=400&fit=fill&fm=webp&q=90`}
                    quality={90}
                    width={photo.width}
                    height={photo.height}
                    alt={photo.title}
                    aria-label={photo.description}
                    priority
                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                  />
                </MainImageContainer>
              </Container>
            </MainImageWrapper>
          )}
          {/* <ContentMarkdown content={fullBody} /> */}
          <RichTextWrapper>
            <Container>
              <TableOfContents>
                {vertical.slug === 'medicare' && slug === 'flex-cards-for-seniors' && (
                  <RxCTASection>
                    <SectionHeading>PolicyScout Member Benefits</SectionHeading>
                    <RxIconWrapper>
                      <SvgRxIcon />
                    </RxIconWrapper>
                    <H3>
                      Save up to $95 on each prescription with our <br />
                      <span>Free Rx Discount Card</span> <sup>*</sup>
                    </H3>
                    <TextBody>
                      PolicyScout has negotiated for you discounted Rx prices across
                      35,000 pharmacies nationally.
                    </TextBody>
                    <Link href="/rx/download" legacyBehavior>
                      <StyledAnchor width="241px" size="large">
                        Get Your Free Rx Card
                      </StyledAnchor>
                    </Link>
                  </RxCTASection>
                )}
                <KeyTakeaways keyTakeaways={keyTakeaways} />
                <RichTextContainer>
                  <RichTextResponse
                    richTextResponse={fullBody}
                    data={{ vertical: vertical.slug }}
                  />
                </RichTextContainer>
              </TableOfContents>
            </Container>
          </RichTextWrapper>
        </article>
      </main>
    </Layout>
  )
}

export default Post

export async function getStaticPaths() {
  await wait()
  const posts = await getAllArticleStaticPaths()

  const postParams = posts.map((post) => ({
    // using optional chaining as temp fix for running dev-preview
    params: { id: post.slug, vertical: post?.vertical?.slug || '' },
  }))

  return {
    paths: postParams,
    fallback: false,
  }
}

export async function getStaticProps({ params }) {
  await wait()
  const post = await getArticleBySlug(params.id, params.vertical)
  if (!post) {
    return {
      notFound: true,
    }
  }
  await wait()
  const verticals = await getAllVerticals()
  await wait()
  const vertical = await getVertical(params.vertical)

  return { props: { ...post, verticals, vertical } }
}

const RxCTASection = styled.div`
  display: flex;
  align-items: center;
  padding: 3rem 0 2rem;
  margin: 1rem 0 0 3rem;
  border-top: 1px solid ${grabColor('borderContent')};

  ${SectionHeading} {
    display: none;
    margin: 0 auto 2rem;
    color: ${grabColor('brand.dark')};
    @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
      display: block;
    }
  }

  ${TextBody} {
    display: none;
    text-align: center;
    padding: 0 1rem;
    @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
      display: block;
    }
  }

  ${H3} {
    margin: 0 0 0 20px;
    br {
      display: none;
      @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
        display: block;
      }
    }
    sup {
      color: ${grabColor('detail')};
    }
    span {
      color: ${grabColor('rx.DEFAULT')};
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
      margin: 1rem auto;
      text-align: center;
      font-size: 24px;
      line-height: 36px;
    }
  }

  a {
    margin-left: auto;
    @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
      margin: 1rem auto 0;
      width: 100%;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    flex-direction: column;
    margin: 1rem auto 3rem;
    padding: 2rem 1rem 2rem;
  }
`

const RxIconWrapper = styled.div`
  svg {
    width: 48px;
    height: 48px;
    @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
      width: 72px;
      height: 72px;
    }
  }
`

const StyledH1 = styled(H1)`
  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    font-size: 30px;
    line-height: 36px;
  }
`

const StyledSubHeading = styled(SubHeading1)`
  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    font-size: 16px;
  }
`

const HeadingWrapper = styled.div`
  max-width: 800px;
  margin: 2rem auto;
`

const RichTextWrapper = styled.div`
  background: ${grabColor('backgroundLight')};
  padding: 2rem 0;
`

const RichTextContainer = styled.div`
  max-width: 664px;
  width: 100%;
  margin: 0 3rem;

  @media (max-width: ${(props) => props.theme.breakpoints.desktop}) {
    margin: 3rem 0;
  }
`

const MainImageWrapper = styled.div`
  position: relative;
  &:before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 120px;
    background: ${grabColor('backgroundLight')};
  }
`

const MainImageContainer = styled.div`
  position: relative;
  margin-bottom: 3rem;
  margin: 0 auto;
  width: 100%;
  max-width: 1000px;
  height: 400px;
  z-index: 2;
  img {
    width: 100%;
    object-fit: cover;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    height: 208px;
  }
`
